import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingBag, ShieldCheck, RefreshCcw, Truck } from "lucide-react";
import { api, img } from "../lib/api";
import { useLang } from "../context/LangContext";
import { useStore } from "../context/StoreContext";
import { ProductRail } from "../components/store/ProductRail";
import { ProductCard } from "../components/store/ProductCard";
import { Reveal } from "../components/store/Reveal";

const Hero = ({ hero }) => {
  const { L, t } = useLang();
  const title = L(hero, "title") || "قمر | أناقة تليق بكِ";
  const [a, b] = title.split("|").map((s) => s.trim());
  return (
    <section data-testid="hero-section" className="relative h-[calc(100svh-110px)] min-h-[560px] overflow-hidden bg-ink">
      <motion.img initial={{ scale: 1.08 }} animate={{ scale: 1 }} transition={{ duration: 2.4, ease: [0.2, 0.7, 0.2, 1] }}
        src={img(hero?.image || "/images/hero.jpg")} alt="" className="absolute inset-0 h-full w-full object-cover object-[center_25%]" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-black/10" />
      <div className="container-q relative flex h-full flex-col justify-end pb-16 sm:pb-24">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.3 }} className="max-w-3xl text-ivory">
          <p className="eyebrow mb-6 text-gold-soft">QAMAR — {new Date().getFullYear()}</p>
          <h1 data-testid="hero-title" className="text-5xl leading-[1.1] sm:text-6xl lg:text-7xl">
            {a}
            {b && <><span className="mx-4 inline-block h-10 w-px translate-y-1 bg-gold align-middle sm:h-14" /><span className="text-gold-soft">{b}</span></>}
          </h1>
          <p data-testid="hero-subtitle" className="mt-6 max-w-lg text-base leading-8 text-ivory/80 sm:text-lg">«{L(hero, "subtitle")}»</p>
          <Link to={hero?.link || "/shop/all"} data-testid="hero-shop-now-button"
            className="mt-10 inline-flex items-center gap-3 bg-ivory px-10 py-4 text-sm tracking-[0.14em] text-ink transition-colors duration-300 hover:bg-gold-soft">
            {L(hero, "cta") || t("shop_now")} <ShoppingBag size={16} strokeWidth={1.4} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

const CategoryDuo = () => {
  const { categories } = useStore();
  const { L, t } = useLang();
  if (!categories.length) return null;
  return (
    <section className="container-q grid gap-5 pt-20 sm:grid-cols-2 sm:gap-8 sm:pt-28">
      {categories.slice(0, 2).map((c, i) => (
        <Reveal key={c.id} delay={i * 0.12} className={i === 1 ? "sm:mt-24" : ""}>
          <Link to={`/shop/${c.slug}`} data-testid={`category-tile-${c.slug}`} className="group relative block aspect-[4/5] overflow-hidden bg-sand">
            <img src={img(c.image)} alt="" loading="lazy" className="img-zoom h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            <div className="absolute bottom-0 start-0 p-8 text-ivory">
              <p className="eyebrow mb-3 text-gold-soft">0{i + 1} — {t("collection")}</p>
              <h3 className="text-3xl sm:text-4xl">{L(c, "name")}</h3>
            </div>
          </Link>
        </Reveal>
      ))}
    </section>
  );
};

const FeaturedEditorial = ({ products }) => {
  const { t } = useLang();
  if (!products?.length) return null;
  const [first, ...rest] = products;
  return (
    <section data-testid="featured-section" className="grain relative bg-ink py-24 text-ivory sm:py-32">
      <div className="container-q relative z-10 grid gap-10 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-4 lg:pt-10">
          <p className="eyebrow mb-4 text-gold">Featured</p>
          <h2 className="text-4xl leading-tight sm:text-5xl">{t("featured")}</h2>
          <div className="mt-10 hidden max-w-xs lg:block [&_h3]:text-ivory [&_span]:text-ivory/80"><ProductCard p={first} tall /></div>
        </div>
        <div className="grid grid-cols-2 gap-5 sm:gap-8 lg:col-span-8 [&_h3]:text-ivory [&_span]:text-ivory/80">
          <div className="lg:hidden"><ProductCard p={first} /></div>
          {rest.slice(0, 3).map((p, i) => (
            <Reveal key={p.id} delay={i * 0.1} className={i === 1 ? "lg:mt-20" : ""}><ProductCard p={p} /></Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const OfferBanner = ({ banner }) => {
  const { L } = useLang();
  if (!banner) return null;
  return (
    <Reveal className="container-q pt-8">
      <Link to={banner.link || "/shop/offers"} data-testid="offer-banner" className="group relative block h-[420px] overflow-hidden bg-sand">
        <img src={img(banner.image)} alt="" loading="lazy" className="img-zoom h-full w-full object-cover object-[center_30%]" />
        <div className="absolute inset-0 bg-gradient-to-l from-black/70 via-black/20 to-transparent rtl:bg-gradient-to-r" />
        <div className="absolute inset-y-0 start-0 flex max-w-lg flex-col justify-center p-10 text-ivory sm:p-16">
          <p className="eyebrow mb-4 text-gold-soft">Offers</p>
          <h3 className="text-4xl sm:text-5xl">{L(banner, "title")}</h3>
          <p className="mt-4 text-sm text-ivory/80">{L(banner, "subtitle")}</p>
        </div>
      </Link>
    </Reveal>
  );
};

const Features = () => {
  const { t } = useLang();
  const items = [[ShoppingBag, "f_easy"], [ShieldCheck, "f_secure"], [RefreshCcw, "f_returns"], [Truck, "f_ship"]];
  return (
    <section data-testid="features-section" className="border-y border-sand/60">
      <div className="container-q grid grid-cols-2 lg:grid-cols-4">
        {items.map(([Icon, k], i) => (
          <Reveal key={k} delay={i * 0.06} className="border-sand/60 px-2 py-12 sm:px-8 [&:not(:last-child)]:border-e">
            <Icon size={26} strokeWidth={1} className="text-gold-deep" />
            <h4 className="mt-5 font-display text-xl">{t(k)}</h4>
            <p className="mt-2 text-sm leading-6 text-ink/60">{t(`${k}_d`)}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

export default function Home() {
  const { t } = useLang();
  const { settings } = useStore();
  const [data, setData] = useState({});
  useEffect(() => {
    api.get("/home").then((r) => setData(r.data));
  }, []);
  return (
    <div data-testid="home-page">
      <Hero hero={settings?.hero} />
      <CategoryDuo />
      <ProductRail testid="bestsellers-section" eyebrow="Best Sellers" title={t("bestsellers")} to="/shop/bestsellers" products={data.bestsellers} />
      <FeaturedEditorial products={data.featured} />
      <ProductRail testid="new-arrivals-section" eyebrow="New In" title={t("new_arrivals")} to="/shop/new" products={data.new} />
      <OfferBanner banner={settings?.banners?.[0]} />
      <ProductRail testid="offers-section" eyebrow="Offers" title={t("offers")} to="/shop/offers" products={data.offers} />
      <Features />
    </div>
  );
}
